import { findMembershipById, selectMemberships } from "@app/auth";
import type { FreezerActivity } from "@app/freezer";
import { createSelector } from "@app/fx";
import { findRoleById, selectRoles } from "@app/roles";
import {
  appDetailUrl,
  databaseDetailUrl,
  environmentIntegrationsUrl,
} from "@app/routes";
import type { WebState } from "@app/schema";
import type { DeployActivityRow, OperationType } from "@app/types";
import { findUserById, selectUsers } from "@app/users";
import { findAppById, selectApps } from "../app";
import { findDatabaseById, selectDatabases } from "../database";
import { findEndpointById, getEndpointUrl, selectEndpoints } from "../endpoint";
import {
  findEnvById,
  hasDeployEnvironment,
  selectEnvironments,
  selectEnvironmentsByOrg,
} from "../environment";
import { selectExternalAwsAccountsAsList } from "../external-aws-account";
import { findLogDrainById, selectLogDrains } from "../log-drain";
import { findMetricDrainById, selectMetricDrains } from "../metric-drain";
import { selectOperationById, selectOperationsAsList } from "../operation";
import { findPermissionById, selectPermissions } from "../permission";
import {
  findServiceById,
  getScaleTextFromOp,
  selectServices,
} from "../service";

export const selectActivityForTable = createSelector(
  selectOperationsAsList,
  selectEnvironmentsByOrg,
  selectDatabases,
  selectApps,
  selectServices,
  selectLogDrains,
  selectMetricDrains,
  selectExternalAwsAccountsAsList,
  (
    ops,
    envs,
    dbs,
    apps,
    services,
    logDrains,
    metricDrains,
    externalAwsAccounts,
  ): DeployActivityRow[] =>
    ops
      .filter((op) => {
        const env = findEnvById(envs, { id: op.environmentId });
        return hasDeployEnvironment(env);
      })
      .map((op): DeployActivityRow => {
        const env = findEnvById(envs, { id: op.environmentId });
        let resourceHandle = "";
        let resourceDesc = "";
        if (op.resourceType === "app") {
          const app = findAppById(apps, { id: op.resourceId });
          resourceHandle = app.handle;
        } else if (op.resourceType === "database") {
          const db = findDatabaseById(dbs, { id: op.resourceId });
          resourceHandle = db.handle;
          resourceDesc = getScaleTextFromOp(op);
        } else if (op.resourceType === "environment") {
          const env = findEnvById(envs, { id: op.environmentId });
          resourceHandle = env.handle;
        } else if (op.resourceType === "service") {
          const service = findServiceById(services, { id: op.resourceId });
          let url = "";
          if (service.appId !== "") {
            // TODO - temporary until we have a service detail page
            url = appDetailUrl(service.appId);
          } else if (service.databaseId !== "") {
            url = databaseDetailUrl(service.databaseId);
          }
          resourceHandle = service.processType;
          resourceDesc = getScaleTextFromOp(op);
          return {
            ...op,
            envHandle: env.handle,
            resourceHandle,
            resourceDesc,
            url,
          };
        } else if (op.resourceType === "log_drain") {
          const logDrain = findLogDrainById(logDrains, { id: op.resourceId });
          resourceHandle = logDrain.handle;
          const url = environmentIntegrationsUrl(logDrain.environmentId);
          return {
            ...op,
            envHandle: env.handle,
            resourceHandle,
            resourceDesc,
            url,
          };
        } else if (op.resourceType === "metric_drain") {
          const metricDrain = findMetricDrainById(metricDrains, {
            id: op.resourceId,
          });
          resourceHandle = metricDrain.handle;
          const url = environmentIntegrationsUrl(metricDrain.environmentId);
          return {
            ...op,
            envHandle: env.handle,
            resourceHandle,
            resourceDesc,
            url,
          };
        } else if (op.resourceType === "external_aws_account") {
          const account = externalAwsAccounts.find(
            (item) => item.id === op.resourceId,
          );
          resourceHandle =
            account?.accountName || account?.awsAccountId || op.resourceId;
        } else {
          resourceHandle = op.resourceId;
        }

        return { ...op, envHandle: env.handle, resourceHandle, resourceDesc };
      })
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
);

const denyOpTypes: OperationType[] = ["poll"];

export const selectActivityByIdsForTable = createSelector(
  selectActivityForTable,
  (_: WebState, p: { ids: string[] }) => p.ids,
  (ops, opIds) => {
    return ops.filter(
      (op) => opIds.includes(op.id) && !denyOpTypes.includes(op.type),
    );
  },
);

export const selectResourceNameByOperationId = createSelector(
  selectOperationById,
  selectDatabases,
  selectApps,
  selectEndpoints,
  selectEnvironments,
  selectExternalAwsAccountsAsList,
  (op, dbs, apps, enps, envs, externalAwsAccounts) => {
    let resourceHandle = "";
    if (op.resourceType === "app") {
      const app = findAppById(apps, { id: op.resourceId });
      resourceHandle = app.handle;
    } else if (op.resourceType === "database") {
      const db = findDatabaseById(dbs, { id: op.resourceId });
      resourceHandle = db.handle;
    } else if (op.resourceType === "environment") {
      const env = findEnvById(envs, { id: op.environmentId });
      resourceHandle = env.handle;
    } else if (op.resourceType === "vhost") {
      const enp = findEndpointById(enps, { id: op.resourceId });
      resourceHandle = getEndpointUrl(enp);
    } else if (op.resourceType === "external_aws_account") {
      const account = externalAwsAccounts.find(
        (item) => item.id === op.resourceId,
      );
      resourceHandle =
        account?.accountName || account?.awsAccountId || op.resourceId;
    } else {
      resourceHandle = op.resourceId;
    }
    return resourceHandle;
  },
);

export const selectResourceByIdAndType = createSelector(
  [
    (_state: WebState, props: { resourceId: string; resourceType: string }) =>
      props.resourceId,
    (_state: WebState, props: { resourceId: string; resourceType: string }) =>
      props.resourceType,
    (state) => selectDatabases(state),
    (state) => selectApps(state),
    (state) => selectEndpoints(state),
    (state) => selectEnvironments(state),
    (state) => selectServices(state),
    (state) => selectPermissions(state),
    (state) => selectRoles(state),
    (state) => selectUsers(state),
    (state) => selectMemberships(state),
    (state) => selectMetricDrains(state),
    (state) => selectLogDrains(state),
  ],
  (
    resourceId,
    resourceType,
    dbs,
    apps,
    enps,
    envs,
    svcs,
    permissions,
    roles,
    users,
    memberships,
    metricDrains,
    logDrains,
  ) => {
    if (resourceType === "App") {
      const app = findAppById(apps, { id: resourceId });
      if (app.handle) return app;
    }
    if (resourceType === "Database") {
      const db = findDatabaseById(dbs, { id: resourceId });
      if (db.handle) return db;
    }
    if (resourceType === "Account" || resourceType === "Environment") {
      const env = findEnvById(envs, { id: resourceId });
      if (env.handle) return env;
    }
    if (resourceType === "Service") {
      const svc = findServiceById(svcs, { id: resourceId });
      if (svc.id) return svc;
    }
    if (resourceType === "Endpoint") {
      const enp = findEndpointById(enps, { id: resourceId });
      if (enp.id) return enp;
    }
    if (resourceType === "Permission") {
      const permission = findPermissionById(permissions, { id: resourceId });
      if (permission.id) return permission;
    }
    if (resourceType === "Role") {
      const role = findRoleById(roles, { id: resourceId });
      if (role.id) return role;
    }
    if (resourceType === "User") {
      const user = findUserById(users, { id: resourceId });
      if (user.id) return user;
    }
    if (resourceType === "MetricDrain") {
      const metricDrain = findMetricDrainById(metricDrains, { id: resourceId });
      if (metricDrain.id) return metricDrain;
    }
    if (resourceType === "LogDrain") {
      const logDrain = findLogDrainById(logDrains, { id: resourceId });
      if (logDrain.id) return logDrain;
    }
    if (resourceType === "Membership") {
      const membership = findMembershipById(memberships, { id: resourceId });
      if (membership.id) return membership;
    }

    return null;
  },
);

export const selectHasPrimaryResourceForOpByActivity = (
  state: WebState,
  props: { activity: FreezerActivity },
): boolean => {
  return !!selectResourceByIdAndType(state, {
    resourceId: props.activity.primaryResourceId,
    resourceType: props.activity.primaryResourceType,
  });
};
